import { z } from "zod";

const ROLE_NAME_REGEX = /^[A-Za-z0-9&()_\- ]+$/;
const DEPARTMENT_REGEX = /^[A-Za-z0-9&()\/\- ]+$/;

export const roleFormSchema = z.object({
    name: z
        .string()
        .trim()
        .min(1, "Role name is required")
        // 80 and 120 are `roles.name` and `roles.department_scope` in
        // server/app/db/models.py, and the same numbers as the request schema
        // (server/app/api/v2/roles/schema/api.py). The form said 150, so a
        // 100-character name reached the column and answered 500.
        .max(80, "Role name must not exceed 80 characters")
        .regex(
            ROLE_NAME_REGEX,
            "Role name can only contain letters, numbers, spaces, &, -, _, and parentheses"
        ),
    // Optional on the EDIT form only: `PATCH /roles/{id}` leaves the parent
    // alone when the key is absent, so an admin renaming the top-of-tree role
    // (the one role allowed parent_role_id IS NULL) is not asked for a parent
    // it cannot have. Creating a role is the opposite — see
    // `roleCreateFormSchema` below.
    parentRoleId: z
        .string()
        .trim()
        .optional()
        .nullable()
        .transform((v) => (v ? v : "")),

    departmentScope: z
        .string()
        .trim()
        .min(1, "Department is required")
        .max(120, "Department must not exceed 120 characters")
        .regex(
            DEPARTMENT_REGEX,
            "Department can only contain letters, numbers, spaces, &, -, /, and parentheses"
        ),

    description: z
        .string()
        .trim()
        .min(1, "Description is required")
        .max(500, "Description must not exceed 500 characters"),

    permissions: z
        .array(z.string().trim().min(1))
        .optional()
        .default([]),
});

// `create_role` requires a parent and says so in these words
// (server/app/api/v2/roles/service.py). The form said nothing and sent `""`,
// which the request schema then refused as a malformed UUID — so the editor
// read "'' is not a valid UUID" about a dropdown they had left alone.
export const PARENT_ROLE_REQUIRED =
    "Parent Role is required. Choose the role this new role sits under — typically your own role or one below it.";

export const roleCreateFormSchema = roleFormSchema.extend({
    parentRoleId: z
        .string({ message: PARENT_ROLE_REQUIRED })
        .trim()
        .min(1, PARENT_ROLE_REQUIRED),
});

export const roleFormDefaultValues = {
    name: "",
    parentRoleId: "",
    departmentScope: "",
    description: "",
    permissions: [],
};

export const deleteRoleReasonSchema = z.object({
    reason: z
        .string()
        .trim()
        .min(10, "Reason must be at least 10 characters"),
});

export const deleteRoleReasonDefaultValues = {
    reason: "",
};